/**
 * Tournament brackets: single elimination, double elimination and round robin.
 *
 * ## Seeding
 *
 * In a bracket of size 2ᵏ the standard order puts seed 1 and seed 2 in opposite halves
 * so they can meet only in the final, and pairs each seed `s` with `2ᵏ + 1 − s` in the
 * first round (1 v 16, 8 v 9, 4 v 13 … in a bracket of 16). It is built by doubling:
 * start from [1, 2]; to double a list of size m, replace each seed `x` by the pair
 * `x, 2m + 1 − x`. If there are fewer entrants than places, the missing seeds are byes,
 * and because the missing ones are always the highest numbers, the top seeds are the ones
 * who skip round one.
 *
 * ## Structure
 *
 * Matches are described by what fills their two slots: an entrant (by seed), the winner
 * of another match, or the loser of another match. The full bracket is built as if every
 * place were filled and then *compacted*: a match with a bye in one slot is not played,
 * its other occupant just moves on, and references to it are rewritten to point at that
 * occupant. That one step handles byes in single elimination and, in double elimination,
 * the knock-on effect of byes on the losers' bracket. A correct bracket for N entrants
 * has N − 1 matches (single) or 2N − 2 (double, plus an optional reset final); the test
 * suite checks this for every N it can build.
 *
 * ## Round robin
 *
 * The circle method: fix the first team, list the others around a circle, pair opposite
 * positions, then rotate the circle by one place for the next round. Home and away go to whichever team has hosted less so far, which keeps each team's home-minus-away balance within one (two when the number of teams is odd). Every team meets
 * every other exactly once in N − 1 rounds (N − 2 + 1 = N rounds with a bye each if N is
 * odd). A second leg repeats the rounds with home and away swapped.
 */

import { shuffle, type Uint32Source } from "@/lib/tool-kit/random-fun/rng";

export const MAX_ENTRANTS = 64;
export const MAX_ROUND_ROBIN = 32;

/* ------------------------------------------------------------------ seeding */

/** Seeds in bracket order for a bracket of `size` (a power of two, at least 2). */
export function standardSeedOrder(size: number): number[] {
  let order = [1, 2];
  while (order.length < size) {
    const doubled = order.length * 2;
    order = order.flatMap((seed) => [seed, doubled + 1 - seed]);
  }
  return order;
}

export function bracketSize(entrants: number): number {
  let size = 2;
  while (size < entrants) {
    size *= 2;
  }
  return size;
}

/** The entrants in random order. */
export function drawOrder<T>(
  entrants: readonly T[],
  source?: Uint32Source
): T[] {
  return shuffle(entrants, source);
}

/* ------------------------------------------------------------------- matches */

export type Slot =
  | { kind: "entrant"; seed: number }
  | { kind: "winner"; match: string }
  | { kind: "loser"; match: string }
  | { kind: "bye" };

export type Side = "W" | "L" | "G";

export interface Match {
  /** For example `W1-3`, `L2-1` or `GF`. Unique within the bracket. */
  id: string;
  side: Side;
  /** 1-based round within its side, counted after empty rounds are dropped. */
  round: number;
  /** 0 for the last round of this side, 1 for the one before, and so on. */
  fromEnd: number;
  a: Slot;
  b: Slot;
}

export interface Bracket {
  size: number;
  byes: number;
  /** The played matches, in play order. */
  matches: Match[];
}

interface RawMatch {
  id: string;
  side: Side;
  round: number;
  a: Slot;
  b: Slot;
}

const entrant = (seed: number): Slot => ({ kind: "entrant", seed });
const winner = (match: string): Slot => ({ kind: "winner", match });
const loser = (match: string): Slot => ({ kind: "loser", match });
const BYE: Slot = { kind: "bye" };

function winnersRaw(size: number, entrants: number): RawMatch[] {
  const raw: RawMatch[] = [];
  const order = standardSeedOrder(size);
  const seedSlot = (seed: number): Slot =>
    seed <= entrants ? entrant(seed) : BYE;
  let count = size / 2;
  for (let j = 0; j < count; j += 1) {
    raw.push({
      id: `W1-${j + 1}`,
      side: "W",
      round: 1,
      a: seedSlot(order[2 * j] as number),
      b: seedSlot(order[2 * j + 1] as number),
    });
  }
  for (let round = 2; count > 1; round += 1) {
    count /= 2;
    for (let j = 0; j < count; j += 1) {
      raw.push({
        id: `W${round}-${j + 1}`,
        side: "W",
        round,
        a: winner(`W${round - 1}-${2 * j + 1}`),
        b: winner(`W${round - 1}-${2 * j + 2}`),
      });
    }
  }
  return raw;
}

function losersRaw(size: number): RawMatch[] {
  const raw: RawMatch[] = [];
  const k = Math.log2(size);
  const add = (
    round: number,
    count: number,
    make: (j: number) => [Slot, Slot]
  ) => {
    for (let j = 0; j < count; j += 1) {
      const [a, b] = make(j);
      raw.push({ id: `L${round}-${j + 1}`, side: "L", round, a, b });
    }
  };
  add(1, size / 4, (j) => [loser(`W1-${2 * j + 1}`), loser(`W1-${2 * j + 2}`)]);
  for (let m = 2; m <= k; m += 1) {
    const dropCount = size / 2 ** m;
    const dropRound = 2 * m - 2;
    // Reverse the drop-ins on alternate rounds so recent opponents are kept apart.
    add(dropRound, dropCount, (j) => [
      winner(`L${dropRound - 1}-${j + 1}`),
      loser(`W${m}-${m % 2 === 0 ? dropCount - j : j + 1}`),
    ]);
    if (m < k) {
      const mergeRound = 2 * m - 1;
      add(mergeRound, dropCount / 2, (j) => [
        winner(`L${dropRound}-${2 * j + 1}`),
        winner(`L${dropRound}-${2 * j + 2}`),
      ]);
    }
  }
  return raw;
}

/** Collapses byes: returns the played matches with every slot resolved. */
function compact(raw: readonly RawMatch[]): Match[] {
  const byId = new Map(raw.map((match) => [match.id, match]));
  const resolved = new Map<string, { a: Slot; b: Slot; played: boolean }>();

  const resolveSlot = (slot: Slot): Slot => {
    if (slot.kind === "entrant" || slot.kind === "bye") {
      return slot;
    }
    const info = resolveMatch(slot.match);
    if (info.played) {
      return slot;
    }
    const survivor = info.a.kind === "bye" ? info.b : info.a;
    return slot.kind === "winner" ? survivor : BYE;
  };

  const resolveMatch = (id: string) => {
    const known = resolved.get(id);
    if (known) {
      return known;
    }
    const match = byId.get(id) as RawMatch;
    const a = resolveSlot(match.a);
    const b = resolveSlot(match.b);
    const info = { a, b, played: a.kind !== "bye" && b.kind !== "bye" };
    resolved.set(id, info);
    return info;
  };

  const played = raw.filter((match) => resolveMatch(match.id).played);
  // Renumber so the ids and rounds have no gaps.
  const roundsBySide = new Map<Side, number[]>();
  for (const match of played) {
    const rounds = roundsBySide.get(match.side) ?? [];
    if (!rounds.includes(match.round)) {
      rounds.push(match.round);
    }
    roundsBySide.set(match.side, rounds);
  }
  const newRound = (match: RawMatch) =>
    (roundsBySide.get(match.side) as number[]).indexOf(match.round) + 1;
  const countInRound = new Map<string, number>();
  const newId = new Map<string, string>();
  for (const match of played) {
    const key = `${match.side}${newRound(match)}`;
    const next = (countInRound.get(key) ?? 0) + 1;
    countInRound.set(key, next);
    newId.set(match.id, match.side === "G" ? match.id : `${key}-${next}`);
  }
  const rewrite = (slot: Slot): Slot =>
    slot.kind === "winner" || slot.kind === "loser"
      ? { kind: slot.kind, match: newId.get(slot.match) as string }
      : slot;
  const lastRound = new Map(
    [...roundsBySide].map(([side, rounds]) => [side, rounds.length])
  );
  return played.map((match) => {
    const info = resolved.get(match.id) as { a: Slot; b: Slot };
    const round = newRound(match);
    return {
      id: newId.get(match.id) as string,
      side: match.side,
      round,
      fromEnd: (lastRound.get(match.side) as number) - round,
      a: rewrite(info.a),
      b: rewrite(info.b),
    };
  });
}

/** A single-elimination bracket for 2 or more entrants, seeded 1…n in listed order. */
export function singleElimination(entrants: number): Bracket {
  const size = bracketSize(entrants);
  return {
    size,
    byes: size - entrants,
    matches: compact(winnersRaw(size, entrants)),
  };
}

/**
 * A double-elimination bracket for 3 or more entrants. The grand final is the last
 * match; if the entrant from the losers' side wins it, the convention is to play it
 * again, because the winners' side entrant has not yet lost twice.
 */
export function doubleElimination(entrants: number): Bracket {
  const size = bracketSize(entrants);
  const raw = [...winnersRaw(size, entrants), ...losersRaw(size)];
  const lastWinners = `W${Math.log2(size)}-1`;
  const lastLosers = `L${2 * Math.log2(size) - 2}-1`;
  raw.push({
    id: "GF",
    side: "G",
    round: 1,
    a: winner(lastWinners),
    b: winner(lastLosers),
  });
  return { size, byes: size - entrants, matches: compact(raw) };
}

/* -------------------------------------------------------------- round robin */

export interface Pairing {
  /** Team indexes, or null for the team that rests this round. */
  home: number | null;
  away: number | null;
}

/** Rounds of pairings by the circle method; `legs` of 2 repeats them with sides swapped. */
export function roundRobin(teams: number, legs: 1 | 2 = 1): Pairing[][] {
  const slots: (number | null)[] = Array.from({ length: teams }, (_, i) => i);
  if (teams % 2 === 1) {
    slots.push(null);
  }
  const m = slots.length;
  const rounds: Pairing[][] = [];
  // Home minus away so far: the team that has hosted less hosts next.
  const surplus: number[] = new Array(teams).fill(0);
  let circle = slots.slice();
  for (let r = 0; r < m - 1; r += 1) {
    const pairs: Pairing[] = [];
    for (let i = 0; i < m / 2; i += 1) {
      const first = circle[i] ?? null;
      const second = circle[m - 1 - i] ?? null;
      if (first === null || second === null) {
        pairs.push({ home: first, away: second });
        continue;
      }
      const lead = (surplus[first] as number) - (surplus[second] as number);
      const swap = lead > 0 || (lead === 0 && (i + r) % 2 === 1);
      const pair = swap
        ? { home: second, away: first }
        : { home: first, away: second };
      surplus[pair.home] = (surplus[pair.home] as number) + 1;
      surplus[pair.away] = (surplus[pair.away] as number) - 1;
      pairs.push(pair);
    }
    rounds.push(pairs);
    circle = [
      circle[0] ?? null,
      circle[m - 1] ?? null,
      ...circle.slice(1, m - 1),
    ];
  }
  if (legs === 1) {
    return rounds;
  }
  const second = rounds.map((round) =>
    round.map((pair) => ({ home: pair.away, away: pair.home }))
  );
  return [...rounds, ...second];
}

/** How many matches a full round robin has: n(n − 1)/2 for one leg. */
export function roundRobinMatches(teams: number, legs: 1 | 2 = 1): number {
  return (teams * (teams - 1) * legs) / 2;
}
